// The two stylesheets for the renderer's markup (render.ts, classes hx-*).
//
// SCOUT_CSS follows the on-device design system (artifact EUxbPpNEP6LasMNjM6EdPf,
// tokens.css as published 3 Nov 2026): Saira and Azeret Mono, its Day colours
// and the red Night, bands 40 in from both sides, 14 px at the smallest. The
// Scout's WebView is Chrome 101, so no :has(), color-mix(), text-wrap or nested
// rules (styles.test.ts checks). The WebView app sets data-theme="night".
//
// WEB_CSS follows the live vortexmarine.com, whose only source is the
// storefront repo (main 59ad86f, app/styles/v2.css): Montserrat, navy ink on
// white, link-on-light links, teal on navy. The live site has no dark mode;
// help's follows the phone's setting with the site's own navy sections. No text
// under 13 px (the platform's rule).
//
// Fonts are not loaded here: the page around the article loads them (the
// portal's preview from Google Fonts; the Scout from its own copy).

export const SCOUT_CSS = `
:root, [data-theme="day"] {
  --bg-base: #0C1222; --surface: #17213A; --surface-high: #22304F; --surface-inset: #101A30;
  --border: #2E3E60; --border-strong: #4A5D85;
  --text-1: #E2E8F0; --text-2: #A8B8D0; --text-3: #74879F;
  --accent: #38BDF8; --accent-strong: #0EA5E9; --accent-dim: #12405C;
  --caution: #FFB020; --caution-dim: #4A3410;
  --band: var(--surface-inset); --band-edge-light: rgba(226,232,240,.07); --band-edge-dark: rgba(0,0,0,.55);
  --font-ui: "Saira", "Roboto Condensed", "Helvetica Neue", Arial, sans-serif;
  --font-data: "Azeret Mono", "IBM Plex Mono", ui-monospace, monospace;
}
[data-theme="night"] {
  --bg-base: #0A0505; --surface: #140808; --surface-high: #201010; --surface-inset: #0D0505;
  --border: #3A1818; --border-strong: #5C2626;
  --text-1: #FF6B6B; --text-2: #CC5555; --text-3: #884444;
  --accent: #CC5555; --accent-strong: #FF8080; --accent-dim: #3A1414;
  --caution: #D08C3C; --caution-dim: #3A2610;
  --band: var(--surface-inset); --band-edge-light: rgba(255,107,107,.07);
}
body { margin: 0; background: var(--bg-base); color: var(--text-1); font-family: var(--font-ui); }
.hx-article { padding: 20px 0 40px; font-size: 18px; line-height: 1.45; }
.hx-article a { color: var(--accent); }
.hx-article a:hover { color: var(--accent-strong); }
.hx-head { padding: 0 40px; max-width: 780px; }
.hx-head h1 { margin: 0; font-size: 28px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.2; }
.hx-summary { margin: 8px 0 0; color: var(--text-2); }
.hx-label { font-weight: 600; color: var(--text-1); }
.hx-facts { margin: 20px 0 0; }
.hx-facts div, .hx-steps li, .hx-video, .hx-table tr {
  background: var(--band);
  box-shadow: inset 0 1px 0 var(--band-edge-light), inset 0 -1px 0 var(--band-edge-dark);
}
.hx-facts div { display: flex; align-items: baseline; padding: 16px 40px; }
.hx-facts dt { width: 140px; flex: none; font-size: 16px; font-weight: 600; color: var(--text-2); }
.hx-facts dd { margin: 0; }
.hx-safety, .hx-note { margin: 20px 40px 4px; padding: 14px 16px; border-radius: 10px; font-size: 16px; max-width: 780px; box-sizing: border-box; }
.hx-safety { display: flex; gap: 12px; align-items: flex-start; border: 2px solid var(--caution); background: var(--caution-dim); }
/* The design system's triangle-alert icon (stroke 2). Chrome 101 knows only the prefixed mask. */
.hx-safety::before {
  content: ""; width: 24px; height: 24px; flex: none; background: var(--caution);
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3'/%3E%3Cpath d='M12 9v4'/%3E%3Cpath d='M12 17h.01'/%3E%3C/svg%3E") center / contain no-repeat;
  mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3'/%3E%3Cpath d='M12 9v4'/%3E%3Cpath d='M12 17h.01'/%3E%3C/svg%3E") center / contain no-repeat;
}
.hx-note { border: 1px solid var(--border-strong); background: var(--surface); }
.hx-safety p, .hx-note p { margin: 0; }
.hx-safety p { flex: 1; min-width: 0; }
/* The board shows the triangle and caution colours; the word stays for screen readers. */
.hx-safety-k { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.hx-missing { color: var(--caution); }
.hx-body > p, .hx-body > .hx-link { margin: 16px 40px 0; max-width: 780px; }
.hx-body h2 { margin: 0; padding: 24px 40px 12px; font-size: 20px; font-weight: 600; line-height: 1.2; }
.hx-steps { list-style: none; margin: 0; padding: 0; }
.hx-steps li { display: flex; gap: 20px; align-items: flex-start; padding: 16px 40px; }
.hx-n {
  width: 32px; height: 32px; flex: none; display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px; border: 2px solid var(--accent); color: var(--accent); font-family: var(--font-data); font-size: 15px; font-weight: 500;
}
.hx-step { flex: 1; min-width: 0; max-width: 780px; }
.hx-step p { margin: 0; }
.hx-figure { position: relative; margin: 14px 0 0; max-width: 560px; }
.hx-body > .hx-figure { margin: 16px 40px 0; }
.hx-figure img { display: block; max-width: 100%; height: auto; border-radius: 10px; border: 1px solid var(--border); }
.hx-figure figcaption { margin-top: 8px; font-size: 16px; color: var(--text-2); }
.hx-clip picture { display: block; }
.hx-badge { position: absolute; top: 10px; left: 10px; padding: 3px 10px; border-radius: 6px; background: var(--surface-high); color: var(--text-1); font-size: 14px; font-weight: 600; }
.hx-video { margin: 20px 0 0; }
.hx-video-row { display: flex; align-items: center; gap: 20px; padding: 12px 40px; }
.hx-video-thumb { width: 128px; height: 72px; flex: none; border-radius: 8px; background: var(--surface-inset); border: 1px solid var(--border); }
.hx-video-text { flex: 1; min-width: 0; }
.hx-video-title { display: block; font-weight: 600; }
.hx-video-meta { display: block; margin-top: 2px; font-size: 16px; color: var(--text-2); }
.hx-watch {
  height: 48px; padding: 0 20px; border: 2px solid var(--border-strong); border-radius: 10px;
  background: transparent; color: var(--text-1); font: 600 16px var(--font-ui);
}
.hx-watch[disabled] { color: var(--text-3); }
.hx-table { margin: 16px 0 0; overflow-x: auto; }
.hx-table table { border-collapse: collapse; width: 100%; }
.hx-table th, .hx-table td { padding: 12px 16px; text-align: left; font-size: 16px; }
.hx-table th:first-child, .hx-table td:first-child { padding-left: 40px; }
.hx-table th { font-weight: 600; color: var(--text-2); }
.hx-related { padding: 24px 40px 0; }
.hx-related h2 { margin: 0 0 8px; font-size: 20px; font-weight: 600; }
.hx-related ul { margin: 0; padding: 0; list-style: none; }
.hx-related li { padding: 6px 0; }
`;

export const WEB_CSS = `
:root {
  --navy-900: #001a3e; --navy-700: #003680; --teal-400: #01d4c6; --white: #ffffff; --mist-100: #eef3fa;
  --ink: var(--navy-900); --ink-muted: #3a4b66; --link: #0062ea; --rule: #d5deeb; --rule-strong: 2px solid #0050c0;
  --ground: var(--white); --panel: var(--mist-100);
  --font-sans: "Montserrat", "Helvetica Neue", Arial, sans-serif;
}
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    --ink: var(--white); --ink-muted: #c9d6ea; --link: var(--teal-400); --rule: rgba(255,255,255,0.28); --rule-strong: 1px solid rgba(255,255,255,0.28);
    --ground: var(--navy-900); --panel: var(--navy-700);
  }
}
[data-theme="dark"] {
  --ink: var(--white); --ink-muted: #c9d6ea; --link: var(--teal-400); --rule: rgba(255,255,255,0.28); --rule-strong: 1px solid rgba(255,255,255,0.28);
  --ground: var(--navy-900); --panel: var(--navy-700);
}
body { margin: 0; background: var(--ground); color: var(--ink-muted); font: 500 17px/27px var(--font-sans); }
.hx-article { max-width: 768px; margin: 0 auto; padding: 40px 16px 64px; }
.hx-article a { color: var(--link); font-weight: 600; }
.hx-head h1 { margin: 0; color: var(--ink); font: 600 38px/46px var(--font-sans); }
.hx-summary { margin: 12px 0 0; font-size: 19px; line-height: 29px; }
.hx-label { font-weight: 700; color: var(--ink); }
/* As the site's vx-grid3 of vx-ruled blocks, with vx-eyebrow labels. */
.hx-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 24px 40px; margin: 40px 0 0; }
.hx-facts div { display: flex; flex-direction: column; gap: 10px; padding-top: 20px; border-top: var(--rule-strong); }
.hx-facts dt { color: var(--link); font: 700 15px/20px var(--font-sans); }
.hx-facts dd { margin: 0; }
/* The site's note panel: mist on white, navy-700 on navy. */
.hx-safety, .hx-note { margin: 40px 0 0; padding: 24px; background: var(--panel); }
.hx-safety p, .hx-note p { margin: 0; }
.hx-safety-k { color: var(--ink); }
.hx-missing { color: var(--link); }
.hx-body > p, .hx-body > .hx-link { margin: 20px 0 0; }
.hx-body h2 { margin: 40px 0 8px; color: var(--ink); font: 700 26px/34px var(--font-sans); }
.hx-steps { list-style: none; margin: 16px 0 0; padding: 0; }
.hx-steps li { display: flex; gap: 20px; align-items: flex-start; padding: 20px 0; border-top: 1px solid var(--rule); }
.hx-steps li:last-child { border-bottom: 1px solid var(--rule); }
.hx-n { width: 36px; height: 36px; flex: none; display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; background: var(--navy-900); color: var(--white); font-weight: 700; font-size: 16px; }
[data-theme="dark"] .hx-n { background: var(--teal-400); color: var(--navy-900); }
@media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) .hx-n { background: var(--teal-400); color: var(--navy-900); } }
.hx-step { flex: 1; min-width: 0; }
.hx-step p { margin: 4px 0 0; }
.hx-figure { position: relative; margin: 20px 0 0; max-width: 560px; }
.hx-figure img { display: block; max-width: 100%; height: auto; border-radius: 16px; }
.hx-figure figcaption { margin-top: 12px; font-size: 15px; line-height: 22px; }
.hx-clip picture { display: block; }
.hx-badge { position: absolute; top: 16px; left: 16px; padding: 6px 12px; background: var(--navy-900); color: var(--white); font: 700 15px/20px var(--font-sans); }
.hx-video { margin: 28px 0 0; }
.hx-video video { display: block; width: 100%; aspect-ratio: 16 / 9; background: var(--navy-900); }
.hx-video figcaption { margin-top: 12px; font-size: 15px; line-height: 22px; }
.hx-video-title { color: var(--ink); font-weight: 700; }
.hx-table { margin: 20px 0 0; overflow-x: auto; }
.hx-table table { border-collapse: collapse; width: 100%; }
.hx-table th, .hx-table td { padding: 12px 16px 12px 0; text-align: left; border-bottom: 1px solid var(--rule); font-size: 16px; }
.hx-table th { color: var(--ink); font-weight: 700; }
.hx-related { margin: 48px 0 0; padding-top: 24px; border-top: 1px solid var(--rule); }
.hx-related h2 { margin: 0 0 8px; color: var(--ink); font: 700 20px/28px var(--font-sans); }
.hx-related ul { margin: 0; padding: 0; list-style: none; }
.hx-related li { padding: 6px 0; }
/* The site's own step down for phones: a section title is 30px under 760px. */
@media (max-width: 759px) { .hx-head h1 { font-size: 30px; line-height: 38px; } }
@media (min-width: 760px) { .hx-article { padding: 56px 24px 96px; } }
`;
